/**
 * KpiTrio - the S-DASH combined stats (UX-RW-03 v1.1 S-DASH "KPI row").
 *
 * Cards render ONLY when the summary is complete: a skeleton before, a named
 * error with Retry on failure, and on a 503 PartialResult the explicit partial
 * state with NO totals (S6, D-16/R-4). Values are the server's counts verbatim
 * (sums across reachable branches only); each card names its status in words,
 * never by colour alone. The Failed / Pending cards link to the branch's list
 * only for a single-branch TPIN (D-13/D-15) - the landing stays the overview.
 */

import type { PeriodSelection } from '@domain/types'
import type { SummaryView } from '@domain/tpin'
import { PARTIAL_RESULT_TEXT, STATUS_KEYS, STATUS_WORD, kpiLinks, type KpiLinks, type StatusKey } from '@domain/tpinDash'
import { Link } from './Link'
import { ErrorState } from './RealStates'

const GLYPH: Readonly<Record<StatusKey, string>> = { synced: '✓', failed: '!', pending: '…' }

export function KpiTrio({
  view,
  tin,
  shortcutBranch,
  period,
  onRetry,
}: {
  readonly view: SummaryView
  readonly tin: string
  /** The only branch of a single-branch TPIN, when its row has loaded and answered. */
  readonly shortcutBranch: string | null
  readonly period: PeriodSelection
  readonly onRetry: () => void
}) {
  if (view.kind === 'loading') {
    return (
      <div role="status" className="kpi-skeleton" data-testid="kpi-loading">
        Loading summary…
      </div>
    )
  }
  if (view.kind === 'error') {
    return (
      <div data-testid="kpi-error">
        <ErrorState message={view.message} onRetry={onRetry} />
      </div>
    )
  }
  if (view.kind === 'partial') {
    return (
      <div role="alert" className="state state-partial" data-testid="kpi-partial">
        <p>{PARTIAL_RESULT_TEXT}</p>
      </div>
    )
  }

  return (
    <KpiCards
      counts={view.summary}
      label="Combined sync outcomes"
      links={kpiLinks(view.summary, shortcutBranch, `TPIN ${tin}`)}
      period={period}
    />
  )
}

/**
 * The three status cards, shared by S-DASH (combined stats) and S-BRANCH (one
 * branch). A card links only where its link is set; the value is always the
 * server's count, and each card names its status in words.
 */
export function KpiCards({
  counts,
  label,
  links,
  period,
}: {
  readonly counts: Readonly<Record<StatusKey, number>>
  readonly label: string
  readonly links: KpiLinks
  readonly period: PeriodSelection
}) {
  return (
    <ul className="kpi-trio" aria-label={label} data-testid="kpi-trio">
      {STATUS_KEYS.map((key) => {
        const value = counts[key]
        const word = STATUS_WORD[key]
        const link = links[key]
        const emphasis = key === 'failed' && value > 0
        return (
          <li key={key} className={`kpi-card kpi-${key}${emphasis ? ' is-emphasis' : ''}`} data-testid={`kpi-${key}`}>
            <span className="kpi-chip" aria-hidden="true">
              {GLYPH[key]}
            </span>
            <span className="kpi-caption">{word}</span>
            {link === null ? (
              <span className="kpi-value" data-testid={`kpi-value-${key}`}>
                {value}
              </span>
            ) : (
              <Link
                to={link.to}
                period={period}
                className="kpi-value kpi-link"
                testid={`kpi-link-${key}`}
                ariaLabel={link.ariaLabel}
              >
                {value}
              </Link>
            )}
          </li>
        )
      })}
    </ul>
  )
}